import React, { useState, useEffect } from 'react';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import {
  User2, Calendar, Clock, Target, Dumbbell,
  Brain, ArrowLeft, Sparkles, Split, History, Loader, Repeat
} from 'lucide-react';
import { generateWorkoutPlan, getWorkouts } from '../../../services/handlers/serviceHandler';
import { useWorkout } from '../WorkoutContext';
import { buildFollowUpPlanSummary } from '../WorkoutUtils';

const SummaryItem = ({ icon, label, value }) => (
  <div className="flex items-center p-4 rounded-xl"
    style={{
      background: 'linear-gradient(180deg, #ffffff 0%, #f3f3f5 100%)',
      border: '1px solid #e0e0e4',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8), 0 2px 6px rgba(0,0,0,0.05)'
    }}
  >
    <div className="flex items-center justify-center w-10 h-10 rounded-full text-white"
      style={{
        background: 'linear-gradient(180deg, #ff4a4a 0%, #e30b09 60%, #9a0303 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 3px 8px rgba(200,0,0,0.3)'
      }}
    >
      {icon}
    </div>
    <div className="ml-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-semibold text-gray-900">{value}</p>
    </div>
  </div>
);

const StepSix = ({ formData, onBack, onGenerate, updateFields }) => {
  const {
    setWorkoutStrategy,
    showFeedbackHints,
    previousStrategy,
    setShowFeedbackHints,
  } = useWorkout();
  const [error, setError] = useState(null);
  const { userProfile } = useUserProfile();

  const [savedWorkouts, setSavedWorkouts] = useState([]);
  const [loadingSavedWorkouts, setLoadingSavedWorkouts] = useState(false);
  const [followUpEnabled, setFollowUpEnabled] = useState(Boolean(formData.followUpPlan));

  // Default supersets to maxed-out (3) for Advanced lifters, off (0) for everyone
  // else — but only when the user hasn't already chosen on this step.
  useEffect(() => {
    if (formData.supersetsLevel == null) {
      const defaultLevel = formData.experienceLevel === 'Advanced' ? 3 : 0;
      updateFields({ supersetsLevel: defaultLevel });
    }
  }, [formData.experienceLevel, formData.supersetsLevel, updateFields]);

  const supersetOptions = [
    { value: 0, label: 'None', description: 'Standard straight sets only' },
    { value: 1, label: 'A Few', description: '1–2 supersets per workout' },
    { value: 2, label: 'Several', description: 'Roughly half the exercises paired' },
    { value: 3, label: 'All', description: 'Pair every exercise where it makes sense' }
  ];

  useEffect(() => {
    if (!followUpEnabled || savedWorkouts.length || !userProfile?.email) return;
    let cancelled = false;
    setLoadingSavedWorkouts(true);
    getWorkouts(userProfile.email)
      .then(list => {
        if (cancelled) return;
        const sorted = [...(list || [])].sort(
          (a, b) => new Date(b.saveTime || 0) - new Date(a.saveTime || 0)
        );
        setSavedWorkouts(sorted);
      })
      .catch(err => console.error('Error fetching saved workouts:', err))
      .finally(() => { if (!cancelled) setLoadingSavedWorkouts(false); });
    return () => { cancelled = true; };
  }, [followUpEnabled, userProfile?.email, savedWorkouts.length]);

  const handleToggleFollowUp = (enabled) => {
    setFollowUpEnabled(enabled);
    if (!enabled) updateFields({ followUpPlan: null });
  };

  const handleSelectFollowUpPlan = (workoutId) => {
    if (!workoutId) {
      updateFields({ followUpPlan: null });
      return;
    }
    const selected = savedWorkouts.find(w => w.workoutId === workoutId) || null;
    updateFields({ followUpPlan: selected });
  };

  const presetPreferences = [
    "Add cardio at the end of each day",
    "More focus on compound exercises",
    "Avoid cardio",
    "No leg day",
    "Focus on compound exercises only"
  ];

  const handlePresetClick = (preset) => {
    const currentNotes = formData.additionalNotes || "";
    const newNotes = currentNotes
      ? `${currentNotes}, ${preset}`
      : preset;
    updateFields({ additionalNotes: newNotes });
  };

  const handleGenerateWorkout = async () => {
    setError(null);
    setShowFeedbackHints(false);
    setWorkoutStrategy(null);
    onGenerate();
    
    try {
      const followUpPlanSummary = formData.followUpPlan
        ? buildFollowUpPlanSummary(formData.followUpPlan)
        : null;

      const workoutPlan = await generateWorkoutPlan({
        ...formData,
        previousWorkoutStrategy: previousStrategy,
        followUpPlanSummary,
        email: userProfile.email
      });

      updateFields({originalAdditionalNotes: ''})

      const enrichedWorkoutStrategy = {
        ...workoutPlan,
        workoutPlan: {
          ...workoutPlan.workoutPlan,
          notes: {
            food: workoutPlan.notes?.food || '',
            weights: workoutPlan.notes?.weights || '',
            repsAndSets: workoutPlan.notes?.repsAndSets || ''
          }
        },
        notes: {
          food: workoutPlan.workoutPlan.notes?.food || '',
          weights: workoutPlan.workoutPlan.notes?.weights || '',
          repsAndSets: workoutPlan.workoutPlan.notes?.repsAndSets || ''
        }
      };
  
      setWorkoutStrategy(enrichedWorkoutStrategy);
    } catch (err) {
      setError(err.message || 'Failed to generate workout plan.');
      console.error('Workout generation error:', err);
      onBack();
    }
  };

  const summaryData = [
    {
      icon: <User2 className="w-5 h-5" />,
      label: "Profile",
      value: `${formData.name} · ${formData.gender} · ${formData.ageGroup} years`
    },
    {
      icon: <Target className="w-5 h-5" />,
      label: "Goal",
      value: formData.objective
    },
    {
      icon: <Split className="w-5 h-5" />,
      label: "Workout Split",
      value: formData.split
    },
    {
      icon: <Dumbbell className="w-5 h-5" />,
      label: "Focus Areas",
      value: formData.focusAreas.join(", ")
    },
    {
      icon: <Brain className="w-5 h-5" />,
      label: "Experience Level",
      value: formData.experienceLevel
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      label: "Weekly Schedule",
      value: `${formData.daysPerWeek} days per week`
    },
    {
      icon: <Clock className="w-5 h-5" />,
      label: "Session Duration",
      value: `${formData.timePerSession} minutes`
    }
  ];

  return (
    <div className="space-y-6 w-full">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-extrabold aw-title">Review Your Plan</h2>
        <p className="text-gray-500 mt-2">Please review your preferences before generating your workout plan</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {summaryData.map((item, index) => (
          <SummaryItem key={index} {...item} />
        ))}
      </div>

      <div className="rounded-xl p-4"
        style={{
          background: 'linear-gradient(180deg, #fafafa 0%, #f0f0f3 100%)',
          border: '1px solid #e0e0e4',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)'
        }}
      >
        <p className="text-sm text-gray-500 mb-2">Available Equipment</p>
        <div className="flex flex-wrap gap-2">
          {formData.equipment.map((item) => (
            <span key={item} className="aw-chip">{item}</span>
          ))}
        </div>
      </div>

      {formData.limitations && (
        <div className="rounded-xl p-4"
          style={{
            background: 'linear-gradient(180deg, #fff5f5 0%, #ffe1e1 100%)',
            border: '1px solid #f5b8b8',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 6px rgba(154,3,3,0.06)'
          }}
        >
          <p className="text-sm font-bold aw-text-glow-red mb-1">Physical Limitations</p>
          <p className="text-sm text-black/80">{formData.limitations}</p>
        </div>
      )}

      <div className="rounded-xl p-4 space-y-3"
        style={{
          background: 'linear-gradient(180deg, #fafafa 0%, #f0f0f3 100%)',
          border: '1px solid #e0e0e4',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)'
        }}
      >
        <div className="flex items-center gap-2">
          <Repeat className="w-5 h-5 text-red-600" />
          <span className="text-gray-900 font-semibold">Include Supersets</span>
        </div>
        <p className="text-sm text-gray-500">
          Pair exercises back-to-back with no rest between them — bigger pump, denser session.
          {formData.experienceLevel === 'Advanced' && (
            <span className="block mt-1 text-red-600 font-medium">
              Recommended for advanced lifters.
            </span>
          )}
        </p>

        <div className="grid grid-cols-4 gap-2">
          {supersetOptions.map(opt => {
            const selected = formData.supersetsLevel === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateFields({ supersetsLevel: opt.value })}
                className={`aw-btn-3d py-2 rounded-lg text-sm font-semibold
                  ${selected ? 'aw-btn-red' : 'aw-btn-light'}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {formData.supersetsLevel != null && (
          <p className="text-xs text-gray-500 italic">
            {supersetOptions.find(o => o.value === formData.supersetsLevel)?.description}
          </p>
        )}
      </div>

      <div className="rounded-xl p-4 space-y-3"
        style={{
          background: 'linear-gradient(180deg, #fafafa 0%, #f0f0f3 100%)',
          border: '1px solid #e0e0e4',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)'
        }}
      >
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-red-600" />
          <span className="text-gray-900 font-semibold">Follow-up to a previous plan?</span>
        </div>
        <p className="text-sm text-gray-500">
          If you've recently finished a plan, we'll build this one as a natural progression — varying movements and pushing intensity where appropriate.
        </p>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleToggleFollowUp(false)}
            className={`aw-btn-3d px-4 py-2 rounded-lg text-sm font-semibold
              ${!followUpEnabled ? 'aw-btn-red' : 'aw-btn-light'}`}
          >
            No, fresh plan
          </button>
          <button
            type="button"
            onClick={() => handleToggleFollowUp(true)}
            className={`aw-btn-3d px-4 py-2 rounded-lg text-sm font-semibold
              ${followUpEnabled ? 'aw-btn-red' : 'aw-btn-light'}`}
          >
            Yes, follow up
          </button>
        </div>

        {followUpEnabled && (
          <div className="space-y-2">
            {loadingSavedWorkouts ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader className="w-4 h-4 animate-spin" />
                Loading your saved plans…
              </div>
            ) : savedWorkouts.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                You don't have any saved plans yet. Save one first, then come back to follow up on it.
              </p>
            ) : (
              <select
                value={formData.followUpPlan?.workoutId || ''}
                onChange={(e) => handleSelectFollowUpPlan(e.target.value)}
                className="aw-input text-sm"
              >
                <option value="">-- Select a previous plan --</option>
                {savedWorkouts.map(w => (
                  <option key={w.workoutId} value={w.workoutId}>
                    {w.name}
                    {w.saveTime ? ` (${new Date(w.saveTime).toLocaleDateString()})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg relative">
        <label className="block">
          <span className="text-gray-900 font-semibold mb-2 block">Additional Workout Preferences</span>

          {/* Preset Preferences Section */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-2">Quick select preferences:</p>
            <div className="flex flex-wrap gap-2">
              {presetPreferences.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handlePresetClick(preset)}
                  className="aw-preset-btn"
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-2">
            {showFeedbackHints ? (
              <span className="text-red-600">
                Please provide feedback on what you'd like to change in the workout strategy
              </span>
            ) : (
              "Select a quick preference above or type your custom requirements below"
            )}
          </p>

          <textarea
            value={formData.additionalNotes}
            onChange={(e) => updateFields({ additionalNotes: e.target.value })}
            placeholder={showFeedbackHints ?
              "Example: I'd prefer more focus on upper body, less cardio sessions, or different split of muscle groups..." :
              "Your selected preferences will appear here. You can also type your custom requirements..."
            }
            className={`aw-input h-32 resize-none ${showFeedbackHints ? 'border-red-500' : ''}`}
          />
        </label>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm text-red-700"
          style={{
            background: 'linear-gradient(180deg, #fff5f5 0%, #ffe1e1 100%)',
            border: '1px solid #f5b8b8',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)'
          }}
        >
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 mt-8">
        <button
          onClick={handleGenerateWorkout}
          className="aw-btn-3d aw-btn-generate w-full py-5 rounded-2xl font-extrabold text-lg uppercase
            flex items-center justify-center gap-3"
        >
          <Sparkles className="w-6 h-6" />
          <span className="aw-text-3d-white">Generate Workout Plan</span>
          <Sparkles className="w-6 h-6" />
        </button>

        <button
          onClick={onBack}
          className="w-full py-3 rounded-xl font-semibold transition-all duration-200
            flex items-center justify-center gap-2
            text-black hover:text-red-600 hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Edit
        </button>
      </div>
    </div>
  );
};

export default StepSix;